import React, { useState } from 'react';
import { IOrderDetail, IOrderDetailLine } from '../../../actions/order-details/get-order-details.action';
import { NO_DATA_FALLBACK } from '../../../configuration';
import { DELIVERY_INFORMATION_LEG_TYPES, DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS } from '../../../configuration/delivery-information';
import { ILocalizationState } from '../../../states/localization-state';
import { dateLocaleFormatter, toLocalDate } from '../../../utilities';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';
import EventList from './event-list';
import EventProgressBar from './event-progress-bar';

interface DeliveryInformationModalProps {
    orderItemLine: IOrderDetailLine;
    CLASS_NAME: string;
    resources: IOrderDetailsResources;
    localizationState: ILocalizationState;
    deliveryItemCount: number;
    orderDetails: IOrderDetail;
    onPrev: () => void;
    onNext: () => void;
}

export interface DefaultLeg {
    Id: number;
    ModeOfTransportation: string;
    Name: string;
    Events: [{ Name: string; Location: string; Timestamp?: string }];
}

export const createStaticLeg = (id: number, name: string, icon: string, timestamp?: string): DefaultLeg => ({
    Id: id,
    ModeOfTransportation: icon,
    Name: name,
    Events: [{ Name: name, Location: '', Timestamp: timestamp }]
});

const DeliveryInformationModal: React.FC<DeliveryInformationModalProps> = ({
    orderItemLine,
    CLASS_NAME,
    resources,
    localizationState,
    deliveryItemCount,
    orderDetails,
    onPrev,
    onNext
}) => {
    const [showEventInformation, setShowEventInformation] = useState<boolean>(false);
    const {
        DeliveryNumber,
        EstimatedArrivalDate,
        ActualArrivalDate,
        LivetrackingUrl,
        TrackingAvailable,
        PlannedArrivalDate
    } = orderItemLine;

    const mainLeg = orderItemLine.Legs.find(leg => {
        return leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.MainCarriage;
    });

    return (
        <div className={`${CLASS_NAME}__delivery-information`}>
            <input type='hidden' className={`${CLASS_NAME}_division-text`} value={orderDetails.DivisionText} />
            <div className={`${CLASS_NAME}__delivery-information-progress-bar`}>
                <EventProgressBar
                    CLASS_NAME={`${CLASS_NAME}__delivery-information-progress-bar`}
                    orderLine={orderItemLine}
                    resources={resources}
                    localizationState={localizationState}
                    orderDetails={orderDetails}
                    createStaticLeg={createStaticLeg}
                />
            </div>
            <div className={`${CLASS_NAME}__delivery-information-infobox ${CLASS_NAME}__delivery-information-event-list-toggle`}>
                <p
                    className={`${CLASS_NAME}__delivery-information-event-list-toggle-${showEventInformation} fade-slide-right`}
                    onClick={() => setShowEventInformation(!showEventInformation)}
                >
                    {showEventInformation
                        ? resources.orderDetailsModalDeliveryInformationEventListHeading
                        : resources.orderDetailsModalDeliveryInformationShowEventListHeading}
                </p>
                {showEventInformation && (
                    <EventList
                        orderLine={orderItemLine}
                        localizationState={localizationState}
                        CLASS_NAME={`${CLASS_NAME}__delivery-information`}
                        orderDetails={orderDetails}
                        resources={resources}
                    />
                )}
            </div>
            <div className={`${CLASS_NAME}__delivery-information-infobox`}>
                <table className='table table-borderless table-sm mb-0 fade-slide-right'>
                    <tbody>
                        <tr>
                            <td>
                                <strong>{resources.orderDetailsModalDeliveryNumber}</strong>
                            </td>
                            <td>{DeliveryNumber || NO_DATA_FALLBACK}</td>
                        </tr>
                        {!ActualArrivalDate && !EstimatedArrivalDate && !PlannedArrivalDate ? null : !ActualArrivalDate ? (
                            <tr>
                                <td>
                                    <strong>
                                        {EstimatedArrivalDate
                                            ? resources.orderDetailsModalEstimatedDeliveryDate
                                            : resources.orderDetailsModalPlannedArrivalDate}
                                    </strong>
                                </td>
                                <td>
                                    {EstimatedArrivalDate
                                        ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(EstimatedArrivalDate))
                                        : PlannedArrivalDate
                                        ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(PlannedArrivalDate))
                                        : null}
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td>
                                    <strong>{resources.orderDetailsModalActualArrivalDate}</strong>
                                </td>
                                <td>{dateLocaleFormatter(localizationState).formatDate(toLocalDate(ActualArrivalDate))}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className={`${CLASS_NAME}__delivery-information-infobox`}>
                <p className={`${CLASS_NAME}__delivery-information-infobox-main-carriage-heading fade-slide-right`}>
                    {resources.orderDetailsModalMainCarriageHeading}
                </p>
                <table className='table table-borderless table-sm fade-slide-right'>
                    <tbody>
                        {mainLeg?.CarrierName && (
                            <tr>
                                <td>
                                    <strong>{resources.orderDetailsModalCarrierName}</strong>
                                </td>
                                <td>{mainLeg?.CarrierName || NO_DATA_FALLBACK}</td>
                            </tr>
                        )}
                        {mainLeg?.Container && (
                            <tr>
                                <td>
                                    <strong>{resources.orderDetailsModalContainer}</strong>
                                </td>
                                <td>{mainLeg?.Container || NO_DATA_FALLBACK}</td>
                            </tr>
                        )}
                        {mainLeg?.ContainerType && (
                            <tr>
                                <td>
                                    <strong>{resources.orderDetailsModalContainerType}</strong>
                                </td>
                                <td>{mainLeg?.ContainerType || NO_DATA_FALLBACK}</td>
                            </tr>
                        )}
                        {mainLeg?.ModeOfTransportation &&
                            (mainLeg.ModeOfTransportation.toUpperCase() === DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.OCEAN ||
                                mainLeg.ModeOfTransportation.toUpperCase() === DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.FLIGHT) && (
                                <tr>
                                    <td>
                                        {DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS[mainLeg.ModeOfTransportation.toUpperCase()] ===
                                            DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.OCEAN && (
                                            <strong>{resources.orderDetailsModalShipNumber}</strong>
                                        )}
                                        {DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS[mainLeg.ModeOfTransportation.toUpperCase()] ===
                                            DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.FLIGHT && (
                                            <strong>{resources.orderDetailsModalFlightNumber}</strong>
                                        )}
                                    </td>
                                    <td>{mainLeg?.ShipFlightNumber || NO_DATA_FALLBACK}</td>
                                </tr>
                            )}
                        {mainLeg?.BlNumber && (
                            <tr>
                                <td>
                                    <strong>{resources.orderDetailsModalBLnumber}</strong>
                                </td>
                                <td>{mainLeg.BlNumber || NO_DATA_FALLBACK}</td>
                            </tr>
                        )}
                        <tr>
                            <td>
                                <strong>{resources.orderDetailsModalLiveTracking}</strong>
                            </td>
                            <td>
                                {TrackingAvailable ? (
                                    <a
                                        href={LivetrackingUrl}
                                        aria-label={resources.orderDetailsModalLiveTracking}
                                        rel='noreferrer'
                                        target='_blank'
                                    >
                                        {resources.orderDetailsModalLiveTrackingAvailable}
                                    </a>
                                ) : (
                                    <span>{resources.orderDetailsModalNoLiveTrackingAvailable}</span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {deliveryItemCount > 1 && (
                <div className={`${CLASS_NAME}__delivery-information-button-group`}>
                    <button onClick={onPrev} className='msc-cta__primary ms-order-details__delivery-information-button-previous'>
                        <span className={`${CLASS_NAME}__delivery-information-button-previous-icon`} />
                    </button>
                    <button onClick={onNext} className='msc-cta__primary ms-order-details__delivery-information-button-next'>
                        <span className={`${CLASS_NAME}__delivery-information-button-next-icon`} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default DeliveryInformationModal;
